import { attachCanvasRenderQueue } from "./canvas-render-queue";
import { attachMetadataEditingOverlay } from "./metadata-editing-overlay";
import { applyGraphicsPreview } from "./graphics-preview";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { loadArtFonts } from "../../lib/schedule-art/art-fonts";
import Konva from "konva";
import { loadPlatformImages } from "../../lib/schedule-art/platform-images";
import { moveMetadataElement } from "./metadata-drag";
import { attachCustomArt } from "./custom-art-controls";
import { attachDevOverlay } from "./dev-overlay-controls";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScheduleStore } from "../../stores/schedule-store";
import { dispatchScheduleEvent } from "../../lib/schedule-history/dispatch";
import { loadArtImage, scheduleArtSources } from "../../lib/schedule-art/art-assets";
import { ART_HEIGHT, ART_WIDTH } from "../../lib/schedule-art/art-canvas";
import { renderArtText } from "./art-text";
import { renderOriginMarkers } from "./origin-markers";
import { getCanvasState } from "../../lib/schedule-art/canvas-state";
import { builderSessionId, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { findImage } from "../../lib/schedule-history/image-registry";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";

const canvasFields = [
  "preset", "week", "slots", "commonOptions", "profile", "canvasMetadata", "artistHandlePosition",
  "artistHandleScale", "artistHandleRotation", "offlineMessages", "offlineActive", "developerMode", "anchorsVisible",
] as const;

function moveArtistHandle(point: number[]) {
  const position = point.map(value => Math.round(value * 100) / 100);
  dispatchScheduleEvent({ type: "handle/setPosition", position });
}

export function ScheduleArtCanvas() {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const preset = useScheduleStore(state => state.preset);

  useEffect(() => {
    const element = container.current!;
    const frame = viewport.current!;
    const stage = new Konva.Stage({ container: element });
    let disposed = false;
    setFailed(false);
    let editing: ReturnType<typeof attachMetadataEditingOverlay> | undefined;
    const resize = () => {
      const scale = Math.min(frame.clientWidth / ART_WIDTH, frame.clientHeight / ART_HEIGHT);
      if (scale <= 0) return;
      const width = ART_WIDTH * scale;
      const height = ART_HEIGHT * scale;
      if (stage.width() === width && stage.height() === height) return;
      stage.size({ width, height });
      stage.scale({ x: scale, y: scale });
      editing?.resize();
      // Paint the resized artwork before the browser displays the new layout.
      stage.draw();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(frame);
    const graphics = new Konva.Layer({ listening: false });
    stage.add(graphics);
    const layer = new Konva.Layer();
    stage.add(layer);
    const unsubscribeArt = attachCustomArt(stage);
    const unsubscribeDevOverlay = attachDevOverlay(stage);
    const nodes: Konva.Image[] = [];
    const textGroup = new Konva.Group({ name: "metadata-text" });
    layer.add(textGroup);
    const originMarkers = new Konva.Group();
    layer.add(originMarkers);
    editing = attachMetadataEditingOverlay(stage, t);
    const images = new Map<string, HTMLImageElement>();
    const updateImages = () => {
      const builderActive = useMetadataBuilderStore.getState().active;
      if (disposed || getCanvasState().preset.id !== preset.id) return;
      if (!builderActive && !images.size) return;
      const state = getCanvasState();
      const artwork = useMetadataHistoryStore.getState().sessions[builderSessionId]?.artwork;
      const allBuilderSources = artwork ? scheduleArtSources(artwork, state.week, state.slots) : [];
      const previewDays = state.previewDays;
      const isolateDays = previewDays !== null && allBuilderSources.length > 0;
      const builderSources = isolateDays ? allBuilderSources.filter((_, index) => index === 0 || previewDays.includes(weekdayKeys[index - 1]!)) : allBuilderSources;
      const sources = builderActive ? builderSources : scheduleArtSources(preset, state.week, state.slots);
      sources.forEach((source, index) => {
        const image = builderActive ? findImage(source)! : images.get(source)!;
        const node = nodes[index];
        if (node) {
          if (node.image() !== image) node.image(image);
        } else {
          const next = new Konva.Image({
            image, x: 0, y: 0, width: ART_WIDTH, height: ART_HEIGHT, listening: false,
          });
          nodes.push(next);
          graphics.add(next);
        }
      });
      while (nodes.length > sources.length) nodes.pop()!.destroy();
      applyGraphicsPreview(graphics);
      renderArtText(
        textGroup,
        state,
        t("art.offline"),
        state.developerMode ? moveMetadataElement : undefined,
        builderActive ? undefined : moveArtistHandle,
      );
      const hasMovableArtistHandle = state.profile.artist_handle.length > 0;
      textGroup.listening(state.developerMode || hasMovableArtistHandle);
      textGroup.moveToTop();
      renderOriginMarkers(originMarkers, state.developerMode && state.anchorsVisible ? state.canvasMetadata ?? state.preset.metadata : null, state.builderDayLayout, state.previewDays);
      originMarkers.moveToTop();
      editing?.refresh();
    };
    const redraw = attachCanvasRenderQueue(stage, updateImages);
    let fontVersion = 0;
    const refreshFonts = () => {
      const version = ++fontVersion;
      const state = getCanvasState();
      const metadata = state.canvasMetadata ?? state.preset.metadata;
      loadArtFonts(metadata).then(() => {
        if (!disposed && version === fontVersion) redraw.request();
      }).catch(() => { if (!disposed && version === fontVersion) setFailed(true); });
    };
    const unsubscribe = useScheduleStore.subscribe((state, previous) => {
      const canvasChanged = canvasFields.some(key => state[key] !== previous[key]);
      if (canvasChanged) redraw.request();
      if (state.canvasMetadata?.styles !== previous.canvasMetadata?.styles) refreshFonts();
    });
    const unsubscribeView = useMetadataEditingStore.subscribe((state, previous) => {
      if (state.graphicsOpacity !== previous.graphicsOpacity || state.grayscale !== previous.grayscale) applyGraphicsPreview(graphics);
    });
    const unsubscribeBuilder = useMetadataBuilderStore.subscribe((state, previous) => {
      if (state.step !== previous.step || state.previewModes !== previous.previewModes || state.sampleArtistHandle !== previous.sampleArtistHandle || state.sampleOfflineMessage !== previous.sampleOfflineMessage || state.previewDay !== previous.previewDay || state.sampleSlots !== previous.sampleSlots || state.sampleCommonOptions !== previous.sampleCommonOptions || state.sampleDate !== previous.sampleDate || state.sampleTimezone !== previous.sampleTimezone || state.sampleOfflineActive !== previous.sampleOfflineActive) redraw.request();
      if (state.active === previous.active) return;
      setFailed(false);
      redraw.request();
      refreshFonts();
    });
    document.fonts.addEventListener("loadingdone", refreshFonts);
    const unsubscribeMetadata = useMetadataHistoryStore.subscribe((state, previous) => {
      if (!useMetadataBuilderStore.getState().active) return;
      if (state.sessions[builderSessionId] === previous.sessions[builderSessionId]) return;
      redraw.request();
      refreshFonts();
    });
    const sources = [preset.base, ...preset.single, ...preset.double, ...preset.offline];
    Promise.all([Promise.all(sources.map(loadArtImage)), loadArtFonts(preset.metadata), loadPlatformImages()]).then(([loaded]) => {
      if (disposed) return;
      sources.forEach((source, index) => images.set(source, loaded[index]!));
      redraw.request();
      refreshFonts();
    }).catch(() => { if (!disposed && !useMetadataBuilderStore.getState().active) setFailed(true); });
    // A builder starts on an empty canvas and does not wait for preset artwork.
    refreshFonts();
    return () => {
      disposed = true;
      redraw.dispose();
      stage.find<Konva.Group>("Group").forEach(node => node.off(".canvasDrag"));
      unsubscribe();
      unsubscribeView();
      unsubscribeBuilder();
      document.fonts.removeEventListener("loadingdone", refreshFonts);
      unsubscribeMetadata();
      editing?.dispose();
      element.style.cursor = "";
      unsubscribeArt();
      unsubscribeDevOverlay();
      observer.disconnect();
      stage.destroy();
    };
  }, [preset, t]);

  return (
    <div ref={viewport} className="relative grid min-h-0 w-full flex-1 items-start justify-items-center overflow-hidden rounded border border-neutral-200 bg-neutral-50" role="group" aria-label={t("schedule.preview")}>
      <div ref={container} />
      {failed && <p role="alert" className="absolute inset-0 grid place-items-center p-4 text-sm text-red-600">{t("art.loadError")}</p>}
    </div>
  );
}
